// src/pages/markets/board/card/CardHeader.tsx
//
// Image left, everything textual right of it. The image slot is always
// rendered (placeholder when the event has none) so the title column starts at
// the same x in every card — a 24/7 monitor lives off stable scan positions.
//
// The image is the link to the event page; the chevron folds the card back to
// a row. Nothing else in the header is clickable.

import { Box, IconButton, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { ScreenerEvent } from '../api/boardTypes';
import { CARD_TEST_IDS, HEADER_HEIGHT } from './cardLayout';
import { formatTimeLeft, formatUsd } from './cardFormat';
import { EventImageLink } from './EventImageLink';
import { EventOpenLink } from './EventOpenLink';
import { HoverPanel } from './HoverPanel';
import { numSx } from '../../../../components/common/page/numberStyles';

const STATUS_LABELS: Record<string, string> = {
    open: '',
    closing_soon: 'closing soon',
    closed: 'closed',
    resolved: 'resolved',
};

const IMAGE_SIZE = 40;
const MAX_HEADER_TAGS = 3;

function Dot() {
    return (
        <Typography sx={{ fontSize: '11px', color: 'text.disabled', flexShrink: 0 }}>·</Typography>
    );
}

const PANEL_OPEN_DELAY_MS = 150;

const metaSx = {
    fontSize: '11px',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    flexShrink: 0,
} as const;

export function CardHeader({ event, now, onToggle, onOpenEvent, badge, panel }: {
    event: ScreenerEvent;
    now: number;
    onToggle?: (e: React.MouseEvent) => void;
    onOpenEvent?: (e: React.MouseEvent) => void;
    badge?: React.ReactNode;
    panel?: () => React.ReactNode;
}) {
    const status = STATUS_LABELS[event.resolution_status] ?? event.resolution_status;
    const marketLabel = event.market_count === 1 ? 'market' : 'markets';
    const tagLine = event.tags.slice(0, MAX_HEADER_TAGS).filter(Boolean).join(' · ');

    return (
        <Box
            data-testid={CARD_TEST_IDS.header}
            onClick={onToggle}
            style={{ height: HEADER_HEIGHT }}
            sx={{
                px: 1.25,
                pt: 0.75,
                pb: 0.5,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflow: 'hidden',
                // The strip folds the card; the image inside it navigates and
                // stops propagation, so it keeps its own pointer meaning.
                cursor: onToggle ? 'pointer' : 'default',
            }}
        >
            <EventImageLink
                event={event}
                size={IMAGE_SIZE}
                radius="6px"
                fallbackFontSize="14px"
                onOpenEvent={onOpenEvent}
            />

            <HoverPanel
                disabled={!panel}
                placement="right-start"
                padded={false}
                openDelayMs={PANEL_OPEN_DELAY_MS}
                content={panel ?? null}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignSelf: 'stretch',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
                    <Typography
                        sx={{
                            minWidth: 0,
                            fontSize: '13px',
                            fontWeight: 600,
                            lineHeight: 1.25,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {event.title ?? event.markets[0]?.question ?? event.event_id}
                    </Typography>
                    <EventOpenLink event={event} onOpenEvent={onOpenEvent} />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minWidth: 0,
                        overflow: 'hidden',
                    }}
                >
                    <Typography sx={{ ...metaSx, ...numSx }}>{formatUsd(event.volume)}</Typography>
                    <Dot />
                    <Typography sx={metaSx}>
                        {status || formatTimeLeft(event.end_date, now)}
                    </Typography>
                    <Dot />
                    <Typography
                        sx={{
                            ...metaSx,
                            flexShrink: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {event.market_count} {marketLabel}
                        {event.open_market_count < event.market_count
                            ? ` (${event.open_market_count} open)`
                            : ''}
                    </Typography>
                    {tagLine && (
                        <>
                            <Dot />
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.disabled',
                                    flex: '1 1 0',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {tagLine}
                            </Typography>
                        </>
                    )}
                </Box>
            </HoverPanel>

            {onToggle && (
                <IconButton
                    size="small"
                    onClick={onToggle}
                    aria-label="Fold to row"
                    sx={{ p: 0.25, flexShrink: 0, alignSelf: 'flex-start' }}
                >
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                </IconButton>
            )}
        </Box>
    );
}